import React, { useState } from "react";
import Reveal from "@/components/Reveal";
import { Plus } from "lucide-react";
import { fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const toneMap = { lavanda: "text-lavanda", gramur: "text-gramur", ocean: "text-ocean", neutru: "text-[#4A4844]" };

const filters = [
  { key: "all", label: "Tot" },
  { key: "detergent", label: "Detergenți" },
  { key: "degresant", label: "Degresanți" },
  { key: "balsam", label: "Balsam" },
];

export default function ProductGrid({ products, onOpen }) {
  const { addItem } = useCart();
  const [active, setActive] = useState("all");
  const list = active === "all" ? products : products.filter((p) => p.category === active);

  return (
    <section id="colectie" className="py-24 md:py-32 border-t border-black/10" data-testid="product-grid-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <p className="text-xs tracking-[0.5em] uppercase text-gramur mb-4">Colecția completă</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-none tracking-tight">
              Întregul <span className="italic text-[#4A4844]">atelier</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {filters.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setActive(f.key)}
                className={`px-7 py-3.5 text-sm font-semibold tracking-widest uppercase rounded-full border-2 transition-all duration-300 ${
                  active === f.key
                    ? "bg-[#1E7A3D] text-white border-[#1E7A3D] shadow-[0_10px_25px_-8px_rgba(30,122,61,0.6)] scale-105"
                    : "bg-white border-[#0E0F12]/20 text-[#0E0F12] hover:border-[#1E7A3D] hover:text-[#1E7A3D] hover:-translate-y-0.5"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.05}>
              <article data-testid={`product-card-${p.slug}`} className="group">
                <div
                  className="relative aspect-square overflow-hidden rounded-sm border border-black/10 bg-white cursor-pointer"
                  onClick={() => onOpen(p)}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-contain p-3 md:p-4 group-hover:scale-[1.03] transition-transform duration-[1200ms]"
                  />
                  {p.old_price && (
                    <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase bg-gramur text-[#0E0F12] px-3 py-1 rounded-full">
                      Ofertă
                    </span>
                  )}
                  <button
                    data-testid={`grid-add-${p.slug}`}
                    onClick={(e) => { e.stopPropagation(); addItem(p); }}
                    className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#0E0F12] text-[#EDEAE3] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500"
                    aria-label="Adaugă în coș"
                  >
                    <Plus className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="mt-5 text-center">
                  <h3 className="font-sans font-semibold text-base md:text-lg leading-snug text-[#0E0F12]">{p.name}</h3>
                  <div className="mt-2 flex items-center justify-center gap-3">
                    {p.old_price && (
                      <span className="text-sm md:text-base text-[#4A4844] line-through">{fmtPrice(p.old_price)}</span>
                    )}
                    <span data-testid={`price-${p.slug}`} className="font-serif font-semibold text-3xl md:text-4xl leading-none text-[#C0392B]">
                      {fmtPrice(p.price)}
                    </span>
                  </div>
                  <button
                    data-testid={`order-now-${p.slug}`}
                    onClick={(e) => { e.stopPropagation(); onOpen(p); }}
                    className="mt-3 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1E7A3D] text-white text-sm font-medium rounded-sm hover:bg-[#176230] hover:-translate-y-[2px] transition-all duration-300"
                  >
                    Comandă Acum
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
